import { default as React, useEffect, useRef } from 'react';
import styled from '@emotion/styled';

import { BundlePurchaseItemList } from '@common/checkout-utils';
import { useDayJs } from '@common/hooks';
import { Drawer } from '@common/overlay-manager';
import { Gray_Light_White } from '@common/styles';
import {
  ComingSoon,
  CommonView,
  Container,
  createOrderSheetInPDP,
  GlobalSizeGuideButton,
  OnlyCustomOptionView,
  useCountdown,
  useOptions,
} from '@widgets/select-option';
import { useIsAuthenticated, useZpayOptionLog } from '@domains/pdp-common';

interface Props {
  extraContent?: React.ReactNode;
  use_portal?: boolean;
  productId?: string;
  open: boolean;
  onEdit?: () => void;
  onClose?: () => void;
  onAddCart?: (bundle_purchase_item_list: BundlePurchaseItemList) => void;
}

/**
 * 특정 티징 상품 대상으로 재구매 알림 비활성화 처리 추가
 */
const TEASING_IDS = new Set([
  '158002204',
  '158784639',
  '158991877',
  '158991951',
  '158991991',
  '158992288',
  '158992347',
  '158992352',
  '158992363',
  '158992375',
  '158992437',
  '158992441',
  '158992448',
  '158992451',
  '158992456',
]);

export const SelectOptionsScreen: React.FC<React.PropsWithChildren<Props>> = ({
  extraContent,
  use_portal = true,
  open,
  onEdit,
  productId,
  onClose = () => {
    // empty
  },
  onAddCart = () => {
    // empty
  },
}) => {
  const dayjs = useDayJs();
  const { pageviewUBL } = useZpayOptionLog();
  const { isLoggedIn } = useIsAuthenticated();
  const {
    isFetched,
    options: {
      catalog_product: { id: product_id, product_option_list, custom_input_option_list, scheduled_sale_date },
    },
  } = useOptions(productId);
  const { is_time_passed } = useCountdown({ target_timestamp: scheduled_sale_date });
  const formattedDate = dayjs(scheduled_sale_date).format('M월 D일 HH:mm');
  const purchasedItems = useRef('');
  const drawerOverflow = extraContent ? 'visible' : 'hidden';
  const language = 'ko';
  const disabledAutoSelect = !!scheduled_sale_date;
  const disabledNotification = TEASING_IDS.has(product_id);

  useEffect(() => {
    if (!open) {
      return;
    }

    // MEMO: 네이티브 PDP에서는 적재하지 않고 웹 PDP에서만 로그 전송하도록 use_portal 조건 추가
    if (use_portal) {
      pageviewUBL(product_id);
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, use_portal]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (sessionStorage) {
      const items = sessionStorage.getItem(`${product_id}?purchased-items`);

      if (items) {
        purchasedItems.current = items;
      }

      Object.keys(sessionStorage).forEach((key) => {
        if (key.includes('?purchased-items')) {
          sessionStorage.removeItem(key);
        }
      });
    }

    if (isLoggedIn && purchasedItems.current) {
      const purchase = async () => {
        const items = JSON.parse(purchasedItems.current);
        await createOrderSheetInPDP(items);
      };
      purchase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  // TODO: 에러 발생으로 옵션 item list가 없는 경우 대응해야 함 (기획/디자이너단 상의 필요)
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!product_id) {
    return null;
  }

  const View = () => {
    const onlyTextOption = !product_option_list.length && custom_input_option_list.length > 0;

    if (onlyTextOption) {
      return <OnlyCustomOptionView isLoggedIn={isLoggedIn} onClose={onClose} onAddCart={onAddCart} />;
    }

    return (
      <CommonView
        isLoggedIn={isLoggedIn}
        onClose={onClose}
        onAddCart={onAddCart}
        onEdit={onEdit}
        disabledAutoSelect={disabledAutoSelect}
        disabledNotification={disabledNotification}
      />
    );
  };

  const render_content = (
    <Container>
      {isFetched ? (
        <PdpSelectOptions>
          {language !== 'ko' && <GlobalSizeGuideButton />}
          {View()}
          {!is_time_passed && <ComingSoon date={formattedDate} />}
        </PdpSelectOptions>
      ) : null}
    </Container>
  );

  if (use_portal) {
    return (
      <Drawer open={open} onClose={onClose} maxHeight={'calc(100vh - 16px)'} height='65vh' overflow={drawerOverflow}>
        {open && extraContent}
        {render_content}
      </Drawer>
    );
  }

  return render_content;
};

const PdpSelectOptions = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${Gray_Light_White};
  border-radius: 8px 8px 0 0;
  overflow: hidden;
`;
